import axios from "axios";
import { API_BASE_URL, LOGO_BASE_URL } from "../../../apiConfig";
import { useState, useEffect } from "react";
import { ChevronDown, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import useBreakpoint from '../../../hooks/useBreakpoint';

function PointsTable() {
    const [points, setPoints] = useState([]);
    const [season, setSeason] = useState("");
    const [divisions, setDivisions] = useState([]);
    const [selectedDiv, setSelectedDiv] = useState("137680");
    const [loading, setLoading] = useState(true);
    const { isMobile, isTablet } = useBreakpoint();

    function fetchPoints(id = 137680) {
        setLoading(true);
        axios.get(`${API_BASE_URL}/pointsTable/${id}`)
            .then((res) => {
                setPoints(res.data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }

    useEffect(() => {
        axios.get(`${API_BASE_URL}/count`).then((res) => setSeason(res.data.season));
        axios.get(`${API_BASE_URL}/currentDivision`).then((res) => {
            if (res.data && res.data.length > 0) {
                setDivisions(res.data);
                setSelectedDiv(res.data[0].PCID);
                fetchPoints(res.data[0].PCID);
            } else {
                fetchPoints();
            }
        });
    }, []);

    function handleChange(e) {
        const id = e.target.value;
        setSelectedDiv(id);
        fetchPoints(id);
    }

    const FONT_RAJ = "'Rajdhani', sans-serif";
    const FONT_BODY = "'Barlow', sans-serif";
    const FONT_DISPLAY = "'Barlow Condensed', sans-serif";

    // Column configuration
    const cols = isMobile
        ? [
            { label: 'P', key: 'column_2', w: 40 },
            { label: 'W', key: 'column_3', w: 40 },
            { label: 'L', key: 'column_4', w: 40 },
            { label: 'PTS', key: 'column_13', w: 50, bold: true, color: '#E7C424' }
          ]
        : [
            { label: 'Matches', key: 'column_2', w: 80 },
            { label: 'Won', key: 'column_3', w: 60 },
            { label: 'Loss', key: 'column_4', w: 60 },
            { label: 'NRR', key: 'column_5', w: 80 },
            { label: 'Points', key: 'column_13', w: 80, bold: true, color: '#E7C424' }
          ];

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            padding: isMobile ? '0px 16px' : '0px 24px',
            gap: '16px',
            maxWidth: 'var(--maxWidth)',
            margin: '0 auto',
            width: '100%',
            boxSizing: 'border-box',
            marginBottom: '60px',
        }}>
            {/* Header section */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                minHeight: '44px',
                flexWrap: 'wrap',
                gap: '16px',
            }}>
                <h2 style={{
                    fontFamily: 'var(--fontDisplay)',
                    fontSize: isMobile ? '22px' : '28px',
                    fontWeight: 800,
                    lineHeight: '1.2',
                    color: '#000',
                    whiteSpace: 'nowrap',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    margin: 0
                }}>
                    Points Table
                </h2>

                {/* Styled Dropdown */}
                <div style={{
                    boxSizing: 'border-box', display: 'flex', alignItems: 'center',
                    padding: isMobile ? '8px 16px' : '12px 24px',
                    gap: '16px',
                    width: isMobile ? '140px' : '176px',
                    height: isMobile ? '38px' : '44px',
                    border: '1.5px solid #060D8A', position: 'relative', flexShrink: 0,
                    borderRadius: '4px'
                }}>
                    <select
                        value={selectedDiv}
                        onChange={handleChange}
                        style={{
                            position: 'absolute', inset: 0, width: '100%', height: '100%',
                            opacity: 0, cursor: 'pointer', zIndex: 2,
                        }}>
                        {divisions.map((d) => (
                            <option key={d.PCID} value={d.PCID}>{d.name}</option>
                        ))}
                    </select>
                    <span style={{ 
                        fontFamily: FONT_RAJ, fontWeight: 700, 
                        fontSize: isMobile ? '14px' : '16px', 
                        color: '#060D8A', pointerEvents: 'none',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>
                        {divisions.find(d => d.PCID == selectedDiv)?.name || 'Division'}
                    </span>
                    <ChevronDown size={14} color="#060D8A" style={{ pointerEvents: 'none' }} />
                </div>
            </div>

            {/* Table Container */}
            <div style={{
                width: '100%',
                background: '#FFFFFF',
                borderRadius: '0px',
                overflow: 'hidden',
                border: '1px solid #E5E7EB',
            }}>
                {/* Table Header Row */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: isMobile ? '12px' : '12px 24px',
                    background: '#040960',
                }}>
                    <div style={{ flex: 1, minWidth: isMobile ? '120px' : '200px' }}>
                        <span style={{ 
                            fontFamily: FONT_DISPLAY, fontWeight: 700, 
                            fontSize: isMobile ? '13px' : '15px', 
                            color: '#FFFFFF', 
                            textTransform: 'uppercase', letterSpacing: '1px' 
                        }}>
                            Team
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: isMobile ? '8px' : '16px' }}>
                        {cols.map((col) => (
                            <div key={col.label} style={{ width: `${col.w}px`, textAlign: 'center' }}>
                                <span style={{ 
                                    fontFamily: FONT_DISPLAY, fontWeight: 700, 
                                    fontSize: isMobile ? '13px' : '15px', 
                                    color: '#FFFFFF', 
                                    textTransform: 'uppercase' 
                                }}>
                                    {col.label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Table Data Rows */}
                <div style={{ 
                    display: 'flex', flexDirection: 'column', 
                    opacity: loading ? 0.5 : 1, transition: 'opacity 0.3s' 
                }}>
                    {(points.length > 0 ? points : Array(8).fill(null)).map((row, i) => (
                        <motion.div 
                            key={i}
                            whileHover={{ backgroundColor: '#F1F3F9', x: 4 }}
                            transition={{ type: 'tween', duration: 0.2 }}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                padding: isMobile ? '10px 12px' : '12px 24px',
                                borderBottom: '1px solid #E5E7EB',
                                background: i % 2 === 0 ? '#FFFFFF' : '#F9FAFB',
                            }}
                        >
                            {/* Team Info with Logo */}
                            <div style={{ 
                                flex: 1, display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '16px',
                                minWidth: isMobile ? '120px' : '200px' 
                            }}>
                                <div style={{
                                    width: isMobile ? '24px' : '32px',
                                    height: isMobile ? '24px' : '32px',
                                    background: '#fff',
                                    borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                    overflow: 'hidden',
                                    border: '1.5px solid #E5E7EB',
                                }}>
                                    {row?.team_logo ? (
                                        <img 
                                            src={`${LOGO_BASE_URL}/${row.team_logo}`} 
                                            alt={row.column_1}
                                            style={{ width: '80%', height: '80%', objectFit: 'contain' }}
                                        />
                                    ) : (
                                        <Trophy size={isMobile ? 14 : 18} color="#040960" style={{ opacity: 0.2 }} />
                                    )}
                                </div>
                                <span style={{
                                    fontFamily: FONT_DISPLAY, fontWeight: 700,
                                    fontSize: isMobile ? '14px' : '16px',
                                    color: '#040960',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    textTransform: 'uppercase'
                                }}>
                                    {row ? row.column_1 : '—'}
                                </span>
                            </div>

                            {/* Stats */}
                            <div style={{ display: 'flex', gap: isMobile ? '8px' : '16px' }}>
                                {cols.map((col) => (
                                    <div key={col.label} style={{ width: `${col.w}px`, textAlign: 'center' }}>
                                        <span style={{ 
                                            fontFamily: FONT_DISPLAY, fontWeight: 700, 
                                            fontSize: isMobile ? '14px' : '16px', 
                                            color: '#040960',
                                            opacity: row ? 1 : 0.2
                                        }}>
                                            {row ? (row[col.key] ?? '—') : '—'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default PointsTable;
